import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

function Artifacts() {
  const [artifacts, setArtifacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);

  // Fetch user's completed lessons and their artifacts
  useEffect(() => {
    const fetchUserArtifacts = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          setError("User not authenticated");
          setLoading(false);
          return;
        }

        // Get user data to find organization_id
        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
        if (!userDoc.exists()) {
          setError("User data not found");
          setLoading(false);
          return;
        }

        // Fetch completed lessons from user_activity
        const activityRef = collection(db, "user_activity");
        const q = query(
          activityRef,
          where("user_id", "==", doc(db, "users", currentUser.uid)),
          where("type", "==", "completed_lesson")
        );
        const querySnapshot = await getDocs(q);

        const completedLessonIds = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.lesson_id) {
            completedLessonIds.push(data.lesson_id);
          }
        });

        console.log("Found completed lessons:", completedLessonIds);

        // Fetch lesson details for completed lessons
        const artifactsData = [];
        for (const lessonId of completedLessonIds) {
          try {
            const lessonRef = doc(db, "node_lessons", lessonId);
            const lessonDoc = await getDoc(lessonRef);

            if (lessonDoc.exists()) {
              const lessonData = lessonDoc.data();

              // Only include lessons that have an artifact image
              if (lessonData.artifact_image) {
                artifactsData.push({
                  id: lessonId,
                  title: lessonData.title || "Untitled Lesson",
                  artifact_image: lessonData.artifact_image,
                  description: lessonData.description || "",
                });
              }
            }
          } catch (lessonError) {
            console.error(`Error fetching lesson ${lessonId}:`, lessonError);
          }
        }

        // Sort artifacts by title for consistent ordering
        artifactsData.sort((a, b) => a.title.localeCompare(b.title));

        setArtifacts(artifactsData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching user artifacts:", error);
        setError("Failed to load artifacts");
        setLoading(false);
      }
    };

    fetchUserArtifacts();
  }, []);

  const handlePrevious = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex > 0 ? prevIndex - 1 : artifacts.length - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex < artifacts.length - 1 ? prevIndex + 1 : 0
    );
  };

  const handleArtifactClick = (index) => {
    setCurrentIndex(index);
  };

  // Calculate scale and opacity for each artifact
  const getArtifactStyle = (index) => {
    const distance = Math.abs(index - currentIndex);
    const maxDistance = Math.min(3, Math.floor(artifacts.length / 2)); // Show up to 3 on each side

    if (distance > maxDistance) {
      return { opacity: 0, transform: "scale(0.3)", zIndex: 1 };
    }

    // Center item is larger, others scale down more aggressively
    const scale = distance === 0 ? 1.2 : 1 - distance * 0.25; // Center item 20% larger
    const opacity = 1 - distance * 0.3; // Fade out by 0.3 for each step away
    const zIndex = maxDistance - distance + 1; // Higher z-index for closer items

    return {
      transform: `translateX(-50%) translateY(-50%) scale(${Math.max(
        scale,
        0.3
      )})`,
      opacity: Math.max(opacity, 0.1),
      zIndex,
      transition: "all 0.3s ease-in-out",
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          <p className="mt-2 text-gray-600">Loading your artifacts...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (artifacts.length === 0) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🏺</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            No Artifacts Yet
          </h2>
          <p className="text-gray-600 mb-6">
            Complete some lessons to start collecting artifacts!
          </p>
          <a
            href="/lessons"
            className="bg-primary-600 text-white px-6 py-3 rounded-md hover:bg-primary-700 transition-colors"
          >
            Browse Lessons
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#052828" }}>
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">
            Explorer's Gallery
          </h1>
          <p className="text-gray-300 text-lg">
            Look at all of the treasures you've collected!
          </p>
        </div>

        <p className="sr-only">
          Artifact gallery. When focused, use left and right arrow keys to move
          between artifacts.
        </p>
        {/* Carousel Container */}
        <div
          className="relative h-96 flex items-center justify-center overflow-hidden rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-4 focus-visible:ring-offset-[#052828]"
          tabIndex={0}
          role="region"
          aria-roledescription="carousel"
          aria-label="Collected lesson artifacts"
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft") {
              e.preventDefault();
              handlePrevious();
            } else if (e.key === "ArrowRight") {
              e.preventDefault();
              handleNext();
            }
          }}
        >
          {/* Navigation Buttons */}
          {artifacts.length > 1 && (
            <>
              <button
                type="button"
                onClick={handlePrevious}
                className="absolute left-4 z-20 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-3 rounded-full transition-all duration-200 backdrop-blur-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#052828]"
                aria-label="Previous artifact"
              >
                <ChevronLeftIcon className="w-6 h-6" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={handleNext}
                className="absolute right-4 z-20 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-3 rounded-full transition-all duration-200 backdrop-blur-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#052828]"
                aria-label="Next artifact"
              >
                <ChevronRightIcon className="w-6 h-6" aria-hidden="true" />
              </button>
            </>
          )}

          {/* Artifacts */}
          <div className="relative w-full h-full flex items-center justify-center">
            {artifacts.map((artifact, index) => (
              <div
                key={artifact.id}
                className="absolute cursor-pointer"
                style={{
                  ...getArtifactStyle(index),
                  left: `${50 + (index - currentIndex) * 30}%`,
                  top: "50%",
                  transformOrigin: "center center",
                }}
                onClick={() => handleArtifactClick(index)}
              >
                <div className="relative flex flex-col items-center">
                  <img
                    src={artifact.artifact_image}
                    alt={artifact.title}
                    className="w-64 h-64 object-contain"
                    style={{
                      backgroundColor: "transparent",
                      background: "none",
                    }}
                  />
                  {/* Title at the base */}
                  <div className="mt-4 text-center">
                    <h3 className="text-white font-bold text-lg">
                      {artifact.title}
                    </h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Dots Indicator - Fixed to bottom */}
      {artifacts.length > 1 && (
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 flex justify-center space-x-2 z-50">
          {artifacts.map((_, index) => (
            <button
              key={index}
              onClick={() => handleArtifactClick(index)}
              type="button"
              className={`w-3 h-3 min-w-[24px] min-h-[24px] rounded-full transition-all duration-200 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#052828] ${
                index === currentIndex
                  ? "bg-white scale-125"
                  : "bg-white bg-opacity-50 hover:bg-opacity-75"
              }`}
              aria-label={`Go to artifact ${index + 1}`}
              aria-current={index === currentIndex ? "true" : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default Artifacts;
